// The gate a required placeholder holds the export behind.
//
// A placeholder that was asked to render for an export (onVisible's required pass) has to say when
// its content is on screen, not when it asked for it: the answer is the serialized DOM, and a
// chart that is still animating, still drawing in an effect, or still loading its images
// serializes half-drawn. So every chart under such a placeholder announces itself as it mounts and
// again when it is drawn, and the gate opens on the last of them.
//
// A chart outside a required pass gets a no-op, so the live page is untouched.

import { createContext, useContext, useEffect, useRef } from 'react'

// nothing under a required placeholder announced itself: the content draws in its own mount, so
// one frame after the effects is as late as it can meaningfully be held
export const IDLE_FRAMES = 1

// a chart that announced itself and never finished must not hold the export forever
export const ANNOUNCE_TIMEOUT = 5000

export const RenderGateContext = createContext(null)

const noop = () => {}

// Called by a chart: registers it with the gate above (if any) as it mounts, and returns the
// callback that reports it drawn. Safe to call anywhere -- outside a gate both are no-ops. An
// unmounting chart reports itself, so a chart taken off screen cannot hold the gate.
export const useRenderGate = () => {
  const gate = useContext(RenderGateContext)
  const doneRef = useRef(null)

  if (null === doneRef.current) doneRef.current = gate ? gate.register() : noop

  useEffect(() => doneRef.current, [])

  return doneRef.current
}

// Whether the content is being drawn for an export rather than for the researcher -- what a chart
// asks before spending an animation nobody will see.
export const useIsRenderGated = () => null !== useContext(RenderGateContext)

// Builds the gate a placeholder hands down. `open` is called once: when every chart that
// registered has reported in, when nothing registered at all, or when the wait ran out.
export const createRenderGate = open => {
  const state = { pending: 0, announced: false, sealed: false, opened: false, timer: null, frame: null }

  const clear = () => {
    if (null !== state.timer) clearTimeout(state.timer)
    if (null !== state.frame) cancelAnimationFrame(state.frame)

    state.timer = null
    state.frame = null
  }

  // sealing is what makes the count meaningful: before it, a chart that finished in its own mount
  // effect would open the gate on a sibling that has not registered yet
  const release = () => {
    if (state.opened || !state.sealed) return

    state.opened = true
    clear()
    open()
  }

  const waitOutIdleFrames = () => {
    let frames = IDLE_FRAMES

    const step = () => {
      state.frame = 0 < frames-- ? requestAnimationFrame(step) : null

      if (null === state.frame) release()
    }

    state.frame = requestAnimationFrame(step)
  }

  return {
    register: () => {
      state.pending += 1
      state.announced = true

      let reported = false

      return () => {
        if (reported) return

        reported = true
        state.pending -= 1

        if (0 === state.pending) release()
      }
    },
    // called from the placeholder's own effect, which runs after every child's: whatever
    // registered by now is the subtree the gate waits for
    seal: () => {
      if (state.opened || state.sealed) return

      state.sealed = true

      if (!state.announced) waitOutIdleFrames()
      else if (0 === state.pending) release()
      else state.timer = setTimeout(release, ANNOUNCE_TIMEOUT)
    },
    cancel: clear
  }
}
